import { supabase } from '@/integrations/supabase/client';

/** Where a generated filing is lodged, by hand. */
export const CIPC_EFILING_URL = 'https://eservices.cipc.co.za';

export type CIPCFilingStatus = 'GENERATED' | 'FILED' | 'SUPERSEDED';

export interface CIPCFilingJob {
  id: string;
  company_id: string;
  filing_form: string;
  filing_url: string;
  /** Reference CIPC gave when this was lodged by hand. Null until filed. */
  submission_ref: string | null;
  status: CIPCFilingStatus;
  filed_at: string | null;
  filed_by: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Statutory filing documents generated for a company.
 *
 * Nothing here is submitted to CIPC automatically. The previous
 * `submitToCIPC` posted CoR39 and CoR15.2 forms to an unverified API contract
 * and recorded ACKNOWLEDGED on any 200 response, which could show a filing as
 * lodged when nothing had been. Documents are generated here and filed by hand
 * at {@link CIPC_EFILING_URL}; this table records which, and when.
 */
export async function getCIPCFilingJobs(companyId: string): Promise<CIPCFilingJob[]> {
  const { data, error } = await supabase
    .from('cipc_filing_jobs')
    .select('*')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching CIPC filing records:', error);
    throw error;
  }

  return (data || []) as CIPCFilingJob[];
}

/** Records that a filing document has been generated and awaits manual filing. */
export async function recordGeneratedFiling(
  companyId: string,
  filingForm: string,
  filingUrl: string
): Promise<CIPCFilingJob> {
  const { data, error } = await supabase
    .from('cipc_filing_jobs')
    .insert({
      company_id: companyId,
      filing_form: filingForm,
      filing_url: filingUrl,
      status: 'GENERATED',
      created_by: (await supabase.auth.getUser()).data.user?.id,
    })
    .select()
    .single();

  if (error) {
    console.error('Error recording generated filing:', error);
    throw error;
  }

  return data as CIPCFilingJob;
}

/**
 * Records that a generated filing has been lodged with CIPC by hand.
 *
 * `reference` is what CIPC returned at eservices.cipc.co.za. It is the only
 * evidence the filing happened, so it is required rather than optional — the
 * point of this change is that the system never claims a filing it cannot
 * substantiate.
 */
export async function markFilingLodged(jobId: string, reference: string): Promise<CIPCFilingJob> {
  const trimmed = reference.trim();
  if (!trimmed) {
    throw new Error('A CIPC reference is required to mark a filing as lodged.');
  }

  const { data, error } = await supabase
    .from('cipc_filing_jobs')
    .update({
      status: 'FILED',
      submission_ref: trimmed,
      filed_at: new Date().toISOString(),
      filed_by: (await supabase.auth.getUser()).data.user?.id,
      updated_at: new Date().toISOString(),
    })
    .eq('id', jobId)
    .select()
    .single();

  if (error) {
    console.error('Error marking filing as lodged:', error);
    throw error;
  }

  return data as CIPCFilingJob;
}
